import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// lot AV10 / P3-6 — un déroulant de rubrique (maquette mockup-barre-de-filtres-A-VALIDER.html).
// ⚠️ L'ouverture est pilotée par le parent : ⛔ un seul menu ouvert à la fois, sinon une liste
// de la ligne s'ouvre DERRIÈRE le panneau « Filtres » et devient incliquable.
// ⚠️ Au-dessous de ce seuil, tout tient à l'écran sans défiler : on lit plus vite qu'on ne
// tape, et un champ de recherche au-dessus de deux lignes n'ajoute qu'un doute. Le seuil porte
// sur le nombre de valeurs de la RUBRIQUE (portefeuille), ⛔ pas sur le résultat filtré : le
// champ ne doit pas apparaître et disparaître pendant qu'on coche.
const SEUIL_RECHERCHE = 10;

export const FacetteDeroulante = ({
  cle, titre, vide, accord, options, selection, onToggle, comptagePret,
  nbValeursPortefeuille, nomDansBouton, ouvert, onBasculer, onFermer, enLigne,
}) => {
  const [saisie, setSaisie] = useState('');
  const [aDroite, setADroite] = useState(false);
  const conteneur = useRef(null);
  const menu = useRef(null);

  useEffect(() => {
    if (!ouvert) return undefined;
    const auClic = (e) => { if (conteneur.current && !conteneur.current.contains(e.target)) onFermer(); };
    const auClavier = (e) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => { document.removeEventListener('mousedown', auClic); document.removeEventListener('keydown', auClavier); };
  }, [ouvert, onFermer]);

  // 🔴 Le menu s'ouvre du côté où il y a la place : à gauche de son bouton par défaut, aligné à
  // droite quand il sortirait de la fenêtre. ⛔ Sans ça, le dernier déroulant de la ligne
  // débordait de 14 px et faisait apparaître une barre de défilement horizontale.
  useLayoutEffect(() => {
    if (!ouvert || !menu.current) return;
    const r = menu.current.getBoundingClientRect();
    if (!aDroite && r.right > window.innerWidth - 8) setADroite(true);
    if (aDroite && r.left < 8) setADroite(false);
  }, [ouvert, aDroite, options]);

  const visibles = useMemo(() => {
    const q = saisie.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => String(o.label).toLowerCase().includes(q));
  }, [options, saisie]);

  const nbChoisies = options.filter(o => o.codes.some(c => selection.includes(c))).length;

  // 🔴 La règle « une rubrique à une seule valeur ne s'affiche pas » porte sur le PORTEFEUILLE
  // de l'agence, ⛔ jamais sur le résultat filtré : sinon, plus on filtre, moins on a de
  // filtres — l'utilisateur ne peut plus élargir une recherche trop étroite. Un bouton présent
  // au chargement ne disparaît plus jamais de la barre.
  if (nbValeursPortefeuille <= 1 && nbChoisies === 0) return null;

  const valeurUnique = options.find(o => o.codes.some(c => selection.includes(c)))?.label;
  const valeurTexte = nbChoisies === 0 ? null
    : (nbChoisies === 1 ? valeurUnique : `${nbChoisies} ${accord === 'f' ? 'choisies' : 'choisis'}`);
  // 🔴 Deux comportements selon l'endroit, et c'est voulu : sur la LIGNE il n'y a pas
  // d'intitulé au-dessus, donc le bouton porte le nom de la rubrique ; dans le PANNEAU
  // l'intitulé est déjà là, le répéter ne sert à rien.
  // ⚠️ L'aria-label garde le nom dans les deux cas : un lecteur d'écran n'a pas le repère visuel.
  const etiquetteVisible = valeurTexte ? `${titre} : ${valeurTexte}` : (vide || titre);
  // ⚠️ L'aria-label porte le nom de la rubrique dans les TROIS états, ligne comme panneau :
  // « Toutes » seul ne veut rien dire pour qui n'a pas le repère visuel de l'intitulé — et
  // c'est l'état par défaut, donc le plus fréquent.
  // (sur la ligne, `vide` EST le nom de la rubrique : on ne le dit pas deux fois)
  const etiquetteVocale = valeurTexte
    ? `${titre} : ${valeurTexte}`
    : (vide === titre ? titre : `${titre} : ${vide}`);
  const avecRecherche = nbValeursPortefeuille >= SEUIL_RECHERCHE;

  return (
    <div className={enLigne ? 'w-full' : 'relative shrink-0'} ref={conteneur} data-testid={`facette-${cle}`}>
      <button
        type="button"
        onClick={onBasculer}
        aria-expanded={ouvert}
        aria-label={etiquetteVocale}
        title={etiquetteVisible}
        data-testid={`facette-${cle}-bouton`}
        className={`h-10 px-3 rounded-[10px] text-sm border flex items-center gap-1.5 whitespace-nowrap transition-colors ${
          enLigne ? 'w-full' : 'max-w-[190px]'} ${
          nbChoisies > 0
            ? 'border-[#007BFF] bg-[#EFF6FF] text-[#007BFF] font-medium'
            : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
        }`}
      >
        {/* ⚠️ Le nom de la rubrique ne se tronque jamais ; seule la valeur le fait. */}
        {(!valeurTexte || nomDansBouton) && (
          <span className="shrink-0">{valeurTexte ? `${titre} : ` : (vide || titre)}</span>
        )}
        {valeurTexte && <span className="truncate min-w-0">{valeurTexte}</span>}
        <i className={`bi bi-chevron-down text-xs text-slate-400 shrink-0 transition-transform ${ouvert ? 'rotate-180' : ''}`}></i>
      </button>

      {ouvert && (
        <div
          ref={menu}
          data-testid={`facette-${cle}-liste`}
          // lot CW2 — `enLigne` (téléphone) : la liste descend DANS le flux, pleine largeur et
          // sans défilement propre. En absolu, elle sortait de la feuille, coupée, avec deux
          // ascenseurs imbriqués.
          className={enLigne
            ? 'w-full mt-1.5 bg-white border border-slate-200 rounded-xl p-2'
            : `absolute z-50 top-[calc(100%+6px)] ${aDroite ? 'right-0' : 'left-0'} min-w-[290px] bg-white border border-slate-200 rounded-xl shadow-[0_14px_36px_rgba(15,23,42,0.16)] p-2`}
        >
          {avecRecherche && (
            <input
              type="text"
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              placeholder="Rechercher une valeur…"
              data-testid={`facette-${cle}-recherche`}
              className="w-full h-9 mb-1.5 px-2.5 text-sm border border-slate-200 rounded-lg focus:border-[#007BFF] focus:outline-none"
            />
          )}
          <div className={enLigne ? '' : 'max-h-[248px] overflow-y-auto'}>
            {visibles.map(o => (
              <label
                key={o.value}
                data-testid={`facette-${cle}-option-${o.value}`}
                className="flex items-center gap-2.5 min-h-10 px-2.5 py-2 rounded-lg hover:bg-[#EFF6FF] cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={o.codes.some(c => selection.includes(c))}
                  onChange={() => onToggle(o.codes)}
                  className="w-4 h-4 rounded text-[#007BFF] focus:ring-[#007BFF] border-slate-300"
                />
                <span className="text-sm text-slate-700 flex-1 min-w-0 truncate text-left" title={o.label}>{o.label}</span>
                <span className="text-[13px] text-slate-400 tabular-nums shrink-0"
                      data-testid={`facette-${cle}-compteur-${o.value}`}>
                  {comptagePret ? o.count : '·'}
                </span>
              </label>
            ))}
            {visibles.length === 0 && (
              <p className="text-xs text-slate-400 px-2.5 py-1.5">Aucune valeur ne correspond</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FacetteDeroulante;
